/**
 * SyncMonitor.js - Real-time sync monitoring dashboard
 * Shows frame-by-frame drift metrics and alerts
 */

import React, { useState, useEffect } from 'react';
import axiosInstance from '../../axiosInstance';

const SyncMonitor = ({ sessions, socket }) => {
  const [selectedSession, setSelectedSession] = useState(null);
  const [liveMetrics, setLiveMetrics] = useState([]);
  const [stats, setStats] = useState(null);
  const [displayStatuses, setDisplayStatuses] = useState({});

  useEffect(() => {
    if (!socket || !selectedSession) return;

    // Listen for real-time display status updates
    const handleDisplayStatus = (data) => {
      setDisplayStatuses(prev => ({
        ...prev,
        [data.displayId]: {
          currentFrame: data.currentFrame,
          currentTime: data.currentTime,
          isPlaying: data.isPlaying,
          bufferHealth: data.bufferHealth,
          timestamp: data.timestamp,
        },
      }));

      // Calculate drift
      calculateDrift(data);
    };

    // Listen for metrics updates
    const handleMetricsUpdate = (data) => {
      if (data.sessionId === selectedSession._id) {
        setStats(data.stats);
      }
    };

    socket.on('display:status', handleDisplayStatus);
    socket.on('session:metrics-update', handleMetricsUpdate);

    return () => {
      socket.off('display:status', handleDisplayStatus);
      socket.off('session:metrics-update', handleMetricsUpdate);
    };
  }, [socket, selectedSession]);

  // Fetch session stats periodically
  useEffect(() => {
    if (!selectedSession) return;

    const fetchStats = async () => {
      try {
        const response = await axiosInstance.get(`/video-sync/sessions/${selectedSession._id}/stats`);
        setStats(response.data.stats);
      } catch (error) {
        console.error('[SyncMonitor] Error fetching stats:', error);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 5000); // Every 5 seconds

    return () => clearInterval(interval);
  }, [selectedSession]);

  const calculateDrift = (displayData) => {
    const times = Object.values(displayStatuses).map(d => d.currentTime);
    times.push(displayData.currentTime);

    const avgTime = times.reduce((a, b) => a + b, 0) / times.length;
    const drift = Math.abs(displayData.currentTime - avgTime);

    // Add to metrics
    setLiveMetrics(prev => [
      ...prev.slice(-50), // Keep last 50 measurements
      {
        displayId: displayData.displayId,
        drift,
        timestamp: new Date(),
      },
    ]);

    // Alert if drift exceeds threshold
    if (selectedSession && drift > selectedSession.driftThreshold) {
      console.warn(`[SyncMonitor] Drift alert: ${displayData.displayId} @ ${drift}ms`);
    }
  };

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const millis = ms % 1000;
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${millis.toString().padStart(3, '0')}`;
  };

  const getDriftColor = (drift) => {
    if (drift < 50) return '#27ae60'; // Green - excellent
    if (drift < 100) return '#f39c12'; // Orange - acceptable
    return '#e74c3c'; // Red - needs correction
  };

  const playingSessions = sessions.filter(s => s.status === 'playing');

  return (
    <div className="sync-monitor">
      <div className="monitor-header">
        <h2>Live Sync Monitor</h2>
        {!selectedSession && (
          <p className="subtitle">Select a playing session to monitor</p>
        )}
      </div>

      {/* Session Selector */}
      {playingSessions.length > 0 && (
        <div className="session-selector">
          <label>Monitoring Session:</label>
          <select
            value={selectedSession?._id || ''}
            onChange={(e) => {
              const session = sessions.find(s => s._id === e.target.value);
              setSelectedSession(session);
              setLiveMetrics([]);
              setDisplayStatuses({});
            }}
          >
            <option value="">-- Select Session --</option>
            {playingSessions.map((session) => (
              <option key={session._id} value={session._id}>
                {session.sessionName}
              </option>
            ))}
          </select>
        </div>
      )}

      {playingSessions.length === 0 && (
        <div className="empty-state">
          <p>No sessions are currently playing.</p>
          <p>Start a session from the "Sessions" tab to begin monitoring.</p>
        </div>
      )}

      {/* Monitor Dashboard */}
      {selectedSession && (
        <div className="monitor-dashboard">
          {/* Statistics Summary */}
          {stats && (
            <div className="stats-summary">
              <div className="stat-card">
                <div className="stat-value">{stats.totalChecks.toLocaleString()}</div>
                <div className="stat-label">Total Checks</div>
              </div>
              <div className="stat-card">
                <div className="stat-value" style={{ color: getDriftColor(stats.averageDrift) }}>
                  {Math.round(stats.averageDrift)}ms
                </div>
                <div className="stat-label">Avg Drift</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{Math.round(stats.maxDrift)}ms</div>
                <div className="stat-label">Max Drift</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{Math.round(stats.minDrift)}ms</div>
                <div className="stat-label">Min Drift</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.driftEvents}</div>
                <div className="stat-label">Drift Events</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.corrections}</div>
                <div className="stat-label">Corrections</div>
              </div>
            </div>
          )}

          {/* Display Status Grid */}
          <div className="display-status-grid">
            <h3>Display Status</h3>
            {selectedSession.assignments.map((assignment) => {
              const status = displayStatuses[assignment.display?.displayId];

              return (
                <div key={assignment.display?._id} className="display-status-card">
                  <div className="display-name">
                    📺 {assignment.display?.name || 'Unknown'}
                  </div>

                  {status ? (
                    <>
                      <div className="status-row">
                        <span className="status-label">Time:</span>
                        <span className="status-value">{formatTime(status.currentTime)}</span>
                      </div>
                      <div className="status-row">
                        <span className="status-label">Frame:</span>
                        <span className="status-value">{status.currentFrame.toLocaleString()}</span>
                      </div>
                      <div className="status-row">
                        <span className="status-label">Playing:</span>
                        <span className={`status-badge ${status.isPlaying ? 'playing' : 'paused'}`}>
                          {status.isPlaying ? '▶️ Yes' : '⏸️ No'}
                        </span>
                      </div>
                      <div className="status-row">
                        <span className="status-label">Buffer:</span>
                        <div className="buffer-bar">
                          <div
                            className="buffer-fill"
                            style={{
                              width: `${status.bufferHealth}%`,
                              backgroundColor: status.bufferHealth > 80 ? '#27ae60' : '#f39c12',
                            }}
                          ></div>
                          <span className="buffer-text">{status.bufferHealth}%</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="no-data">
                      <p>Waiting for data...</p>
                      <p className="note">Make sure the custom player app is running on this display</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Live Drift Chart */}
          {liveMetrics.length > 0 && (
            <div className="drift-chart">
              <h3>Live Drift Metrics (Last 50 measurements)</h3>
              <div className="chart-container">
                <div className="chart-yaxis">
                  <span>200ms</span>
                  <span>100ms</span>
                  <span>0ms</span>
                </div>
                <div className="chart-plot">
                  {/* Threshold line */}
                  <div
                    className="threshold-line"
                    style={{
                      bottom: `${(selectedSession.driftThreshold / 200) * 100}%`,
                    }}
                  >
                    <span className="threshold-label">{selectedSession.driftThreshold}ms threshold</span>
                  </div>

                  {/* Data points */}
                  {liveMetrics.map((metric, index) => {
                    const displayName = selectedSession.assignments.find(
                      a => a.display?.displayId === metric.displayId
                    )?.display?.name || metric.displayId;

                    return (
                      <div
                        key={index}
                        className="data-point"
                        style={{
                          left: `${(index / 50) * 100}%`,
                          bottom: `${Math.min((metric.drift / 200) * 100, 100)}%`,
                          backgroundColor: getDriftColor(metric.drift),
                        }}
                        title={`${displayName}: ${Math.round(metric.drift)}ms`}
                      ></div>
                    );
                  })}
                </div>
              </div>

              {/* Recent metrics table */}
              <div className="recent-metrics">
                <h4>Recent Measurements</h4>
                <table>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Display</th>
                      <th>Drift</th>
                    </tr>
                  </thead>
                  <tbody>
                    {liveMetrics.slice(-10).reverse().map((metric, index) => {
                      const displayName = selectedSession.assignments.find(
                        a => a.display?.displayId === metric.displayId
                      )?.display?.name || metric.displayId;

                      return (
                        <tr key={index}>
                          <td>{metric.timestamp.toLocaleTimeString()}</td>
                          <td>{displayName}</td>
                          <td style={{ color: getDriftColor(metric.drift), fontWeight: 'bold' }}>
                            {Math.round(metric.drift)}ms
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SyncMonitor;
